import { useMemo, useRef, useState } from "react";
import { HandCoins, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, newKey, rp, todayJkt } from "@/lib/api";
import { Field, PageHeader, QtyInput, RupiahInput, SectionTitle } from "@/components/common";
import { History } from "@/components/History";

const blank = (products) => products.map((p) => ({ product: p, qty: "", unit: p === "Ampas" ? "Bungkus" : "Papan", note: "", amount: 0 }));

export default function Pemasukan() {
  const { ws, bump } = useAuth();
  const [date, setDate] = useState(todayJkt());
  const [rows, setRows] = useState(() => blank(ws.products));
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const key = useRef(newKey());
  const total = useMemo(() => rows.reduce((s, r) => s + (r.amount || 0), 0), [rows]);
  const set = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const save = async () => {
    const items = rows.filter((r) => r.amount > 0 || Number(r.qty) > 0).map((r) => ({ ...r, qty: Number(r.qty) || 0 }));
    if (!items.length) { toast.error("Isi minimal satu produk yang terjual."); return; }
    const missing = items.find((r) => !r.amount);
    if (missing) { toast.error(`Isi nominal Rp untuk ${missing.product}.`); return; }
    setBusy(true);
    try {
      await api.post("/income", { date, items, client_key: key.current });
      toast.success(`Pemasukan ${rp(total)} tersimpan. Uang Kas sudah bertambah.`);
      setRows(blank(ws.products)); key.current = newKey(); setReload((x) => x + 1); bump();
    } catch (e) { toast.error(errMsg(e)); if (e?.response?.status === 409) { key.current = newKey(); } }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="pemasukan-page">
      <PageHeader icon={HandCoins} title="Pemasukan" subtitle="Catat uang yang benar-benar diterima dari penjualan." />
      <div className="grid xl:grid-cols-[1.15fr_1fr] gap-6">
        <div className="jt-card p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-5">
            <Field label="Tanggal" className="sm:w-56"><input type="date" className="jt-input" value={date} onChange={(e) => setDate(e.target.value)} data-testid="income-date-input" /></Field>
            <p className="text-xs text-muted-foreground sm:pb-3">Belum dibayar? Catat di <b>Kasbon Pelanggan</b>.</p>
          </div>
          <div className="space-y-3">
            {rows.map((r, i) => (
              <div key={r.product} className="rounded-2xl border border-[var(--jt-gold)]/20 bg-white/60 p-3.5 sm:p-4" data-testid={`income-row-${i}`}>
                <div className="font-extrabold text-[var(--jt-emerald)] mb-2.5">{r.product}</div>
                <div className="grid grid-cols-2 sm:grid-cols-[0.8fr_1fr_1.3fr_1.4fr] gap-2.5">
                  <Field label="Jumlah"><QtyInput value={r.qty} onChange={(v) => set(i, { qty: v })} testid={`income-qty-${i}`} /></Field>
                  <Field label="Satuan">
                    <select className="jt-input" value={r.unit} onChange={(e) => set(i, { unit: e.target.value })} data-testid={`income-unit-${i}`}>
                      {ws.product_units.map((u) => <option key={u}>{u}</option>)}
                    </select>
                  </Field>
                  <Field label="Keterangan" className="col-span-2 sm:col-span-1"><input className="jt-input" placeholder="Opsional" value={r.note} onChange={(e) => set(i, { note: e.target.value })} data-testid={`income-note-${i}`} /></Field>
                  <Field label="Rp" className="col-span-2 sm:col-span-1"><RupiahInput value={r.amount} onChange={(v) => set(i, { amount: v })} testid={`income-amount-${i}`} /></Field>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 text-sm">Total diterima: <b className="text-lg text-[#1f7a46]" data-testid="income-total">{rp(total)}</b></div>
            <button className="jt-btn jt-btn-primary sm:min-w-[220px]" disabled={busy} onClick={save} data-testid="income-save-button">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Simpan Pemasukan
            </button>
          </div>
        </div>
        <div className="jt-card p-5 sm:p-6">
          <SectionTitle>Riwayat Pemasukan</SectionTitle>
          <History kinds="income,cust_payment" reloadKey={reload} onChanged={bump} testid="income-history" />
        </div>
      </div>
    </div>
  );
}
